import type { ViewState } from '../game/viewState';
import type { EntityId } from '@fafi/shared';

// D44 Item 8 — aim soft-lock (client input assist ONLY; aimYaw stays a client-authored input, the sim is
// untouched — §3). A magnet, not a snap: while engaged it eases the camera yaw toward the nearest
// candidate; a deliberate mouse flick breaks it. Candidates by context:
//   · my team NOT in possession (or a loose ball) → the BALL (defending)
//   · I am the carrier → teammates within a forward cone (pass targets)
//   · my team has the ball but I'm not the carrier → no assist (free look)
// (The opponent-goal-centre candidate the plan also lists needs attackDir, which isn't plumbed to the
//  client yet — deferred; a carrier aiming at goal simply free-aims, no assist fights it.)

export interface AimAssistConfig {
  ENGAGE_RAD: number; // engage when the aim is within this of a DEFENSE candidate (the ball)
  REENGAGE_RAD: number; // after a break, re-engage only within this (tighter)
  ASSIST_RATE: number; // max pull (rad/s) — a magnet, not a snap
  BREAK_PX: number; // cumulative mouse |dx| within BREAK_MS that breaks the lock
  BREAK_MS: number; // rolling window for the break accumulator
  FWD_CONE: number; // carrier's teammate cone half-width (rad)
  // D45 Item 3a — OFFENSE (I'm the carrier, targeting teammates) is LIGHT: locks only when very close.
  ENGAGE_RAD_OFFENSE: number; // 4° — much tighter than defense
  REENGAGE_OFFENSE: number; // 3°
  OFFENSE_PULL_MUL: number; // 0.6 — 60 % of the defensive pull rate
}

// D46 Item 7: the offensive teammate soft-lock is DISABLED entirely — a carrier free-aims (the goal
// precision-lock, GoalAimLock, is the only carrier assist). The candidate-building code path below is kept
// intact behind this flag so re-enabling is a one-line flip, not a rewrite. The sim-side D34 passRing
// (receiver highlight for Space passes) is a SEPARATE mechanism (softLock.ts) and is unaffected.
const OFFENSE_TEAMMATE_ASSIST = false;

const D = Math.PI / 180;
export const DEFAULT_AIM_ASSIST: AimAssistConfig = {
  ENGAGE_RAD: 10 * D,
  REENGAGE_RAD: 6 * D,
  ASSIST_RATE: 5,
  BREAK_PX: 140,
  BREAK_MS: 150,
  FWD_CONE: 50 * D,
  ENGAGE_RAD_OFFENSE: 4 * D,
  REENGAGE_OFFENSE: 3 * D,
  OFFENSE_PULL_MUL: 0.6,
};

/** D45 Item 3c: the aim-assist master toggle, persisted (default ON). `?noassist` forces it off. */
const AIM_ASSIST_KEY = 'fafi.aimAssist';
export function loadAimAssistEnabled(storage: Pick<Storage, 'getItem'> = localStorage): boolean {
  try {
    return storage.getItem(AIM_ASSIST_KEY) !== 'off';
  } catch {
    return true;
  }
}
export function saveAimAssistEnabled(on: boolean, storage: Pick<Storage, 'setItem'> = localStorage): void {
  try {
    storage.setItem(AIM_ASSIST_KEY, on ? 'on' : 'off');
  } catch {
    /* storage unavailable — ephemeral */
  }
}

export interface AimTarget {
  yaw: number;
  kind: 'ball' | 'teammate';
  id?: EntityId;
  pos: { x: number; y: number };
}

/** Signed smallest angle a→b, in (−π, π]. */
function angleDiff(a: number, b: number): number {
  let d = (a - b) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  else if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export class AimAssist {
  private engaged = false;
  private broken = false;
  private accumPx = 0;
  private windowStart = -1;
  private target: AimTarget | null = null;

  constructor(
    private readonly cfg: AimAssistConfig = DEFAULT_AIM_ASSIST,
    public enabled = true,
  ) {}

  /** The context-dependent candidate targets (pure). */
  candidates(view: ViewState, camYaw: number): AimTarget[] {
    const own = view.entities.find((e) => e.isControlled);
    if (!own) return [];
    const carrier = view.entities.find((e) => e.hasBall);
    const myTeamHasBall = carrier !== undefined && carrier.team === own.team;
    if (!myTeamHasBall) {
      const b = view.ball.pos;
      return [{ yaw: Math.atan2(b.y - own.pos.y, b.x - own.pos.x), kind: 'ball', pos: { x: b.x, y: b.y } }];
    }
    if (!own.hasBall) return []; // teammate carries — free look
    if (!OFFENSE_TEAMMATE_ASSIST) return []; // D46 Item 7: carrier free-aims (offense teammate lock removed)
    const out: AimTarget[] = [];
    for (const e of view.entities) {
      if (e.team !== own.team || e.id === own.id || e.sentOff) continue;
      const yaw = Math.atan2(e.pos.y - own.pos.y, e.pos.x - own.pos.x);
      if (Math.abs(angleDiff(camYaw, yaw)) < this.cfg.FWD_CONE) out.push({ yaw, kind: 'teammate', id: e.id, pos: { x: e.pos.x, y: e.pos.y } });
    }
    return out;
  }

  /** The currently locked target (for the visual marker), or null when not engaged. */
  get locked(): AimTarget | null {
    return this.engaged ? this.target : null;
  }

  /** Advance one frame; returns the (possibly assisted) camera yaw. mouseDxPx = this frame's raw mouse
   *  dx (px); dtSec = frame delta; nowMs = wall clock. Pure w.r.t. the DOM (unit-testable). */
  update(view: ViewState, camYaw: number, mouseDxPx: number, dtSec: number, nowMs: number): number {
    if (!this.enabled) {
      this.engaged = false;
      this.target = null;
      return camYaw;
    }
    // rolling break-accumulator window
    if (this.windowStart < 0 || nowMs - this.windowStart > this.cfg.BREAK_MS) {
      this.windowStart = nowMs;
      this.accumPx = 0;
    }
    this.accumPx += Math.abs(mouseDxPx);

    const cands = this.candidates(view, camYaw);
    if (cands.length === 0) {
      this.engaged = false;
      this.target = null;
      return camYaw;
    }
    let best = cands[0]!;
    let bestErr = Math.abs(angleDiff(camYaw, best.yaw));
    for (const c of cands) {
      const e = Math.abs(angleDiff(camYaw, c.yaw));
      if (e < bestErr) {
        bestErr = e;
        best = c;
      }
    }
    this.target = best;

    // D45 Item 3a: teammate (offense) locks are LIGHT — tighter cones + a gentler pull than the ball (defense) lock.
    const offense = best.kind === 'teammate';
    const engageRad = offense ? this.cfg.ENGAGE_RAD_OFFENSE : this.cfg.ENGAGE_RAD;
    const reengageRad = offense ? this.cfg.REENGAGE_OFFENSE : this.cfg.REENGAGE_RAD;
    const rate = this.cfg.ASSIST_RATE * (offense ? this.cfg.OFFENSE_PULL_MUL : 1);

    if (this.engaged && this.accumPx > this.cfg.BREAK_PX) {
      this.engaged = false; // a deliberate flick breaks the lock
      this.broken = true;
      return camYaw;
    }
    if (this.broken) {
      if (bestErr < reengageRad) {
        this.broken = false;
        this.engaged = true;
      } else return camYaw;
    } else if (!this.engaged) {
      if (bestErr < engageRad) this.engaged = true;
      else return camYaw;
    }
    // engaged: pull toward the target, proportional to the error, capped at rate·dt
    const err = angleDiff(best.yaw, camYaw);
    const maxStep = rate * dtSec;
    return camYaw + Math.max(-maxStep, Math.min(maxStep, err));
  }
}
